/**
 * Protected Route Component
 * Handles authentication and permission checks for routes
 */

import { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';
import { ROUTES } from '../../routes/paths';

interface ProtectedRouteProps {
  children: ReactNode;
  requiredPermissions?: string[];
  requireAll?: boolean;
  requireSuperAdmin?: boolean;
  /** Require at least Execute access to this workflow code (e.g. 'red_dot'). */
  requireWorkflow?: string;
  /**
   * Permissions that satisfy the route even without `requireWorkflow` access —
   * e.g. `action_center:full` holders may open a Red Dot detail to review a
   * queue item before assigning it, without holding red_dot:execute.
   */
  workflowBypassPermissions?: string[];
}

export const ProtectedRoute = ({
  children,
  requiredPermissions = [],
  requireAll = false,
  requireSuperAdmin = false,
  requireWorkflow,
  workflowBypassPermissions = [],
}: ProtectedRouteProps) => {
  const {
    isAuthenticated,
    isLoggingOut,
    user,
    hasAllPermissions,
    hasAnyPermission,
    isSuperAdmin,
    hasWorkflowAccess,
  } = useAuthStore();
  const location = useLocation();

  // A full-page-redirect logout (Entra end-session) is in flight — the browser
  // is about to navigate away. Render nothing rather than <Navigate to="/login">:
  // that internal navigation would paint the login screen before the browser
  // actually commits the redirect, causing a visible flash-then-bounce-to-Entra
  // (see authStore's beginLogout doc comment).
  if (isLoggingOut) {
    return null;
  }

  // Check authentication
  if (!isAuthenticated) {
    // Redirect to login, preserving the attempted location
    return <Navigate to={ROUTES.LOGIN} state={{ from: location }} replace />;
  }

  // Redirect onboarding users to the onboarding flow (super admins skip onboarding entirely)
  if (user?.onboardingComplete === false && !isSuperAdmin()) {
    return <Navigate to={ROUTES.ONBOARDING} replace />;
  }

  // Check super admin requirement
  if (requireSuperAdmin && !isSuperAdmin()) {
    return <Navigate to={ROUTES.UNAUTHORIZED} replace />;
  }

  // Super admins bypass permission checks
  if (isSuperAdmin()) {
    return <>{children}</>;
  }

  // Workflow access gate (at least Execute).
  if (
    requireWorkflow &&
    !hasWorkflowAccess(requireWorkflow) &&
    !(workflowBypassPermissions.length > 0 && hasAnyPermission(workflowBypassPermissions))
  ) {
    return <Navigate to={ROUTES.UNAUTHORIZED} replace />;
  }

  // Check permissions if required
  if (requiredPermissions.length > 0) {
    const hasPermission = requireAll
      ? hasAllPermissions(requiredPermissions)
      : hasAnyPermission(requiredPermissions);

    if (!hasPermission) {
      // User is authenticated but doesn't have permission
      return <Navigate to={ROUTES.UNAUTHORIZED} replace />;
    }
  }

  // User is authenticated and has required permissions
  return <>{children}</>;
};

export default ProtectedRoute;
